// @ts-check

/**
 * @typedef {import('./clauseRenderNode').ClauseNodeRenderFunction} ClauseNodeRenderFunction
 */

/**
 *
 */
const ClauseNodeComponentStyle = {
  marginTop: '8px',
  marginBottom: '8px',
};

/**
 * @param {import('vue').CreateElement} h
 * @param {string} html
 * @param {import('vue').VNodeData=} options
 * @returns {import('vue').VNode}
 */
const renderHtml = (h, html, options = {}) => h('div', {
  ...options,
  domProps: {
    innerHTML: html,
    ...options.domProps,
  },
});


export class ClauseNode {
  static create(node, clause) {
    switch (node.is) {
      case 'html': return new ClauseHtmlNode(node, clause);
      case 'clause_title': return new ClauseTitleNode(node, clause);
      case 'input': return new ClauseInputNode(node, clause);
      default:
        return new ClauseNode(node, clause);
    }
  }


  /**
   * @param {{ is: string; content: string; }} node
   * @param {import('./agreement').Clause} clause
   */
  constructor(node, clause) {
    this.is = node.is;
    this.content = node.content;
    this.parent = clause;
  }

  /**
   * @param {Parameters<ClauseNodeRenderFunction>[0]} h
   * @param {Parameters<ClauseNodeRenderFunction>[1]} context
   * @returns {ReturnType<ClauseNodeRenderFunction>}
   */
  render(h, context) {
    return renderHtml(h, this.content);
  }
}

export class ClauseHtmlNode extends ClauseNode {
  /**
   * @param {Parameters<ClauseNodeRenderFunction>[0]} h
   * @param {Parameters<ClauseNodeRenderFunction>[1]} context
   * @returns {ReturnType<ClauseNodeRenderFunction>}
   */
  render(h, context) {
    return renderHtml(h, this.content, {
      staticStyle: ClauseNodeComponentStyle,
    });
  }
}

/**
 *
 * @param {import('./agreement').Clause} clause
 * @returns {(option: any) => import('./clauseRenderNode').ClauseInputNodeOption}
 */
const createOption = clause => option => ({
  label: option.label,
  value: option.value,
  selected: clause.initialValues.includes(option.value),
});

export class ClauseTitleNode extends ClauseNode {
  /**
   * @param {Parameters<ClauseNodeRenderFunction>[0]} h
   * @param {Parameters<ClauseNodeRenderFunction>[1]} context
   * @returns {ReturnType<ClauseNodeRenderFunction>}
   */
  render(h, context) {
    return h('h2', null, [
      this.content,
      this.parent.isRequired ? null : h('small', [
        h('span', ' '),
        '(Optional)',
      ]),
    ]);
  }
}


/**
 *
 * @param {'radio' | 'checkbox' | 'text'} type
 */
const createWrapUnwrapper = (type) => {
  const passthrough = x => x;
  const unwrap = arrayValue => arrayValue[0];
  const wrap = singleValue => [singleValue];

  switch (type) {
    case 'radio': {
      return {
        wrap,
        unwrap,
      };
    }
    case 'checkbox':
    default: {
      return {
        wrap: passthrough,
        unwrap: passthrough,
      };
    }
  }
};

export class ClauseInputNode extends ClauseNode {
  constructor(node, clause) {
    super(node, clause);
    this.options = (node.options || []).map(createOption(clause));
  }

  /**
   * @param {Parameters<ClauseNodeRenderFunction>[0]} h
   * @param {Parameters<ClauseNodeRenderFunction>[1]} context
   * @returns {ReturnType<ClauseNodeRenderFunction>}
   */
  render(h, context) {
    const { wrap, unwrap } = createWrapUnwrapper(this.parent.type);

    return h(
      `el-${this.parent.type}-group`,
      {
        props: {
          value: unwrap(context.props.value),
        },
        on: {
          /**
           * @param {string | string[]} data if radio, return single string, if checkbox return array of string
           */
          input(data) {
            context.on.input(wrap(data));
          },
        },
      },
      this.options.map(option => this.renderOption(h, option, context)),
    );
  }


  /**
   * @param {Parameters<ClauseNodeRenderFunction>[0]} h
   * @param {import('./clauseRenderNode').ClauseInputNodeOption} option
   * @param {Parameters<ClauseNodeRenderFunction>[1]} context
   * @returns {import('vue').VNode}
   */
  renderOption(h, option, context) {
    const children = renderHtml(h, option.label, {
      style: 'display: inline; line-height: 1.25;',
    });

    const optionNode = h(`el-${this.parent.type}`, {
      props: {
        label: option.value,
      },
    }, [children]);

    return h('div', {
      staticStyle: ClauseNodeComponentStyle,
    }, [optionNode]);
  }
}



// WEBPACK FOOTER //
// ./src/classes/clauseRenderNode.js